import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Link } from '../Link';
import { Article } from 'functions/src/types/firestore/Game/Article';
import { useMobile } from 'src/hooks/useMobile';
import { useDateFormatter } from 'src/hooks/useDateFormatter';
import { ImageOverlayed } from '../ImageOverlayed';
import { NUMERIC_NOTIMEZONE } from 'src/util/dates/presets/times';
import { OptionalWrapper } from '../OptionalWrapper';

export type ArticleCardProps = Article<Date>;

export const ARTICLE_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

export function ArticleCard({
  imgUrl,
  date,
  description,
  title,
  link,
}: ArticleCardProps) {
  const theme = useTheme();
  const isMobile = useMobile();
  const format = useDateFormatter({
    dateFormat: ARTICLE_DATE_FORMAT,
    timeFormat: NUMERIC_NOTIMEZONE,
    separator: ', ',
  });
  const imgWidth = 269;
  const imgHeight = 181;

  // const descriptionTruncated =
  //   description.length > 250 ? `${description.slice(0, 250)}...` : description;
  const dateFormatted = format(date);

  return (
    <OptionalWrapper
      doWrap={!!link}
      Wrapper={
        <Link href={link} target="_blank" sx={{ textDecoration: 'none' }} />
      }
    >
      <Box
        sx={{
          display: 'flex',
          pr: 2,
          ...theme.glow.highlight,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            ml: 1.5,
            my: 1.5,
            mr: 3,
          }}
        >
          <ImageOverlayed
            src={imgUrl}
            width={imgWidth}
            height={imgHeight}
            alt="Article image"
            sx={{
              borderRadius: 2,
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            pt: 2,
          }}
        >
          <Typography
            sx={{
              color: theme.palette.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              textTransform: 'capitalize',
            }}
            variant={isMobile ? 'body1' : 'h5'}
          >
            {title}
          </Typography>
          <Typography
            color={theme.palette.text.secondary}
            variant="body1"
            sx={{
              overflow: 'hidden',
              display: '-webkit-box',
              textOverflow: 'ellipsis',
              WebkitLineClamp: isMobile ? 2 : 4,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {description}
          </Typography>
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'flex-end',
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
              }}
            >
              {dateFormatted}
            </Typography>
          </Box>
        </Box>
      </Box>
    </OptionalWrapper>
  );
}
